import React from "react";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { productCategories, collection } from "../mock";
import useReveal from "../hooks/useReveal";

/**
 * "Shop the collections" — a Tata CLiQ-style category mosaic. Every box
 * floats gently on its own; the box under the cursor dances (bounce + wiggle)
 * while its neighbours lean back, so the grid feels alive. Clicking a box
 * jumps to the full collection with its Quick View cards.
 */
const ACCENTS = ["#2b3aa0", "#c0161c", "#0f766e", "#b45309", "#6d28d9", "#be185d"];

function scrollToCollection() {
  document.querySelector("#collection")?.scrollIntoView({ behavior: "smooth" });
}

export default function CollectionsDance() {
  const [ref, inView] = useReveal();
  const boxes = productCategories.slice(0, 6);

  return (
    <section id="collections" className="kse-section bg-white overflow-hidden">
      <div className="kse-container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <span className="inline-flex items-center gap-2 bg-slate-100 text-[#1a2a6b] text-xs font-bold tracking-[0.2em] px-4 py-1.5 rounded-full mb-5">
              <Sparkles size={14} /> VIEW THE COLLECTIONS
            </span>
            <h2 className="kse-h2">
              Garments by category
            </h2>
            <p className="text-slate-500 mt-3 max-w-xl">
              Move your cursor across the boxes &mdash; each one opens a line we produce for global brands.
            </p>
          </div>
          <button onClick={scrollToCollection} className="kse-btn bg-[#1a2a6b] text-white px-6 py-3 text-sm self-start md:self-auto">
            Browse all {collection.products.length} styles <ArrowUpRight size={16} />
          </button>
        </div>

        <div ref={ref} className={`dance-grid grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 reveal ${inView ? "in-view" : ""}`}>
          {boxes.map((cat, i) => (
            <button
              key={cat.title}
              type="button"
              onClick={scrollToCollection}
              className={`dance-tile group relative text-left rounded-3xl overflow-hidden bg-slate-100 shadow-sm focus:outline-none focus-visible:ring-4 focus-visible:ring-[#2b3aa0]/40 ${
                i === 0 ? "col-span-2 row-span-2 min-h-[320px] sm:min-h-[420px]" : "min-h-[150px] sm:min-h-[200px]"
              }`}
              style={{ "--tile-accent": ACCENTS[i % ACCENTS.length], "--dance-delay": `${i * 0.35}s` }}
              aria-label={`${cat.title} — view collection`}
            >
              <img
                src={cat.image}
                alt={cat.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-[#0d1640]/85 via-[#0d1640]/20 to-transparent" />
              <span className="dance-accent absolute top-0 left-0 h-1.5 w-0 group-hover:w-full transition-all duration-500" />
              <span className="absolute top-3 left-3 bg-white/90 backdrop-blur text-[#1a2a6b] text-[11px] font-bold px-2.5 py-1 rounded-full">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                <span className={`block text-white font-extrabold leading-tight ${i === 0 ? "text-2xl sm:text-4xl" : "text-base sm:text-xl"}`}>
                  {cat.title}
                </span>
                <span className="mt-2 flex flex-wrap gap-1.5 max-h-0 opacity-0 group-hover:max-h-24 group-hover:opacity-100 transition-all duration-500 overflow-hidden">
                  {cat.items.slice(0, i === 0 ? 4 : 2).map((it) => (
                    <span key={it} className="bg-white/15 border border-white/30 text-white text-[11px] px-2 py-0.5 rounded-full backdrop-blur">
                      {it}
                    </span>
                  ))}
                </span>
                <span className="mt-3 inline-flex items-center gap-1 text-blue-100 text-xs font-semibold tracking-wide">
                  VIEW <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
